import { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button, Label } from "reactstrap";
import moment from "moment";

import "../../styles/StackBatch.css";
import { default as utilIcons } from "../../resources/utilIcons";
import { colors } from "../../styles/colors";

import { RestContext } from "../../App";
import { STACKPLOT_URL, DATA_URL } from "../../serverConfig";
import SearchableSelect from "../../components/SearchableSelect";
import StackPlot from "./StackPlot";
import {
	LoadingScreen,
	StackPlotTable,
	StackPlotTimeFrameSelector,
	PlotZoomControls,
} from "./StackPlots";
import { checkMobile } from "../../util/globals";

const INTERVALS = {
	// Number of seconds between data ticks
	hourly: 60 * 60, // 1 hour
	daily: 60 * 60, // 1 hour
	cumulative: 60 * 60, // 1 hour
	"24-hour run": 60 * 60, // 1 hour
	"1-min": 60, // 1 minute
	"5-min": 5 * 60, // 5 minutes
	"15-min": 15 * 60, // 15 minutes
};

function StackBatch({}) {
	// const [columns, setColumns] = useState([]);
	const [selectedBatch, setSelectedBatch] = useState(null);
	const [batchList, setBatchList] = useState([]);
	const [plots, setPlots] = useState([]);
	const [currentIndex, setCurrentIndex] = useState(-1);
	const [selectedConfig, setSelectedConfig] = useState(null);
	const [stackplotData, setStackplotData] = useState({});
	const [config, setConfig] = useState(null);
	const [batchData, setBatchData] = useState([]);
	const [printMode, setPrintMode] = useState(false);
	const [dateRange, setDateRange] = useState({});
	const [chartRefs, setChartRefs] = useState();
	const [loading, setLoading] = useState(false);
	const [loadingProgress, setLoadingProgress] = useState(1);
	const [selectedOptions, setSelectedOptions] = useState({
		selectedSite: null,
		configId: null,
		precision: "hourly",
		datesToLoad: { minTimestamp: null, maxTimestamp: null },
		sameConfig: false,
	});
	const [callbacks, setCallbacks] = useState({});
	const [zoomLevel, setZoomLevel] = useState(100);
	const [popoutWindow, setPopoutWindow] = useState(null);
	const [popoutZoomLevel, setPopoutZoomLevel] = useState(100);

	const loadingOptions = useRef(loading);
	const stackPlotRef = useRef(null);
	const columns = useRef([]);

	const { sendGetRequest, sendPostRequest } = useContext(RestContext);

	useEffect(() => {
		sendGetRequest(STACKPLOT_URL + "/batch/list", (response) => {
			setBatchList(response.data);
		});
	}, []);

	useEffect(() => {
		if (!selectedBatch) return;
		sendGetRequest(
			STACKPLOT_URL + "/batch/" + selectedBatch,
			(response) => {
				const responseData = response.data;

				setPlots(responseData.plots);
			},
		);
	}, [selectedBatch]);

	useEffect(() => {
		if (plots.length) {
			setCurrentIndex(0);
			setSelectedConfig(plots[0].stackPlotId);
		} else {
			setCurrentIndex(-1);
			setSelectedConfig(null);
		}
	}, [plots]);

	useEffect(() => {
		if (!selectedConfig) return;
		sendGetRequest(
			STACKPLOT_URL + "/config/" + selectedConfig,
			(response) => {
				setConfig(response.data);
			},
		);
	}, [selectedConfig]);

	useEffect(() => {
		if (config) {
			const today = moment().hour(0).minute(0).second(0).millisecond(0);
			setSelectedOptions((values) => ({
				...values,
				precision: config.precision || "hourly",
				configId: config.stackPlotId,
				datesToLoad: {
					minTimestamp: moment(today)
						.subtract(config.defaultSpan || 7, "day")
						.unix(),
					maxTimestamp: moment().unix(),
				},
				sameConfig: false,
			}));
		}
	}, [config]);

	useEffect(() => {
		if (config && selectedOptions) {
			beginLoad();
		}
	}, [selectedOptions]);

	useEffect(() => {
		if (loading) {
			loadInfo();
		} else {
			setLoadingProgress(0);
		}
	}, [loading]);

	// useEffect(() => {
	// 	console.log(batchData);
	// }, [batchData]);

	useEffect(() => {
		if (config) {
			reloadStackPlotData();
		}
	}, [config, batchData]);

	// useEffect(() => {
	// 	console.log(stackplotData);
	// }, [stackplotData]);

	// useEffect(() => {
	// 	console.log(config);
	// }, [config]);

	// useEffect(() => {
	// 	console.log(currentIndex, selectedConfig);
	// }, [currentIndex, selectedConfig]);

	useEffect(() => {
		return () => {
			const childWindow = popoutWindow?.ownerDocument?.defaultView;

			if (childWindow && !childWindow.closed) {
				childWindow.close();
			}
		};
	}, [popoutWindow]);

	useEffect(() => {
		if (!popoutWindow || !config?.charts?.length) {
			return;
		}

		const popupDocument = popoutWindow.ownerDocument;
		const popupWindow = popupDocument.defaultView;

		if (!popupWindow) {
			return;
		}

		const updatePopoutZoom = () => {
			const controls = popupDocument.getElementById(
				"stackplot-popout-controls",
			);

			const help = popupDocument.getElementById("stackplot-popout-help");

			const controlsHeight =
				(controls?.getBoundingClientRect().height || 0) +
				(help?.getBoundingClientRect().height || 0);

			const calculatedZoom = calculatePopoutZoom(
				popupWindow,
				config,
				controlsHeight,
			);

			setPopoutZoomLevel(calculatedZoom);
		};

		const frameId = popupWindow.requestAnimationFrame(updatePopoutZoom);

		return () => {
			popupWindow.cancelAnimationFrame(frameId);
		};
	}, [popoutWindow, config]);

	useEffect(() => {
		if (!popoutWindow || !config) {
			return;
		}

		const popupWindow = popoutWindow.ownerDocument?.defaultView;

		if (!popupWindow || popupWindow.closed) {
			return;
		}

		popupWindow.document.title =
			config.fullTitle || config.plotName || "Stack Plot";
	}, [popoutWindow, config]);

	const handlePrev = () => {
		if (currentIndex === -1 || currentIndex === 0) return;
		setSelectedConfig(plots[currentIndex - 1].stackPlotId);
		setCurrentIndex(currentIndex - 1);
	};

	const handleNext = () => {
		if (currentIndex === -1 || currentIndex === plots.length - 1) return;
		setSelectedConfig(plots[currentIndex + 1].stackPlotId);
		setCurrentIndex(currentIndex + 1);
	};

	const handlePlotSelect = (plotId) => {
		let index = -1;
		for (let i = 0; i < plots.length; i++) {
			if (plots[i].stackPlotId === plotId) {
				index = i;
				break;
			}
		}
		setCurrentIndex(index);
		setSelectedConfig(plotId);
	};

	const getDataForCurrentConfig = () => {
		if (
			!batchData ||
			currentIndex === -1 ||
			currentIndex >= batchData.length
		)
			return [];
		return batchData[currentIndex];
	};

	const setDataForCurrentConfig = (data) => {
		if (!batchData || currentIndex === -1) return [];
		const newBatchData = [];
		for (let i = 0; i < plots.length; i++) {
			newBatchData[i] = batchData[i] || null;
		}
		newBatchData[currentIndex] = data;
		setBatchData(newBatchData);
	};

	const reloadStackPlotData = () => {
		const data = getDataForCurrentConfig();
		if (data) {
			setStackplotData(explodeData(data));
		}
	};

	const beginLoad = (dataOptions) => {
		if (!selectedOptions || !config || !config.parCodes) return;
		if (!dataOptions) {
			dataOptions = {
				selectedSite: config.idNo,
				precision: selectedOptions.precision,
				sameConfig: selectedOptions.sameConfig,
				loadedMin: getLoadedMin(getDataForCurrentConfig()),
				loadedMax: getLoadedMax(getDataForCurrentConfig()),
				minTimestamp: selectedOptions.datesToLoad.minTimestamp,
				maxTimestamp: selectedOptions.datesToLoad.maxTimestamp,
				dataType: config.dataType,
				parCodes: config.parCodes,
				calPars: config.calPars,
			};
		}
		if (!loading) {
			setLoading(dataOptions);
		}
	};

	const reloadData = () => {
		if (!selectedOptions || !config || !config.parCodes || !config.calPars)
			return;
		const dataOptions = {
			selectedSite: selectedOptions.selectedSite,
			precision: selectedOptions.precision,
			sameConfig: selectedOptions.sameConfig,
			loadedMin: null,
			loadedMax: null,
			minTimestamp: selectedOptions.datesToLoad.minTimestamp,
			maxTimestamp: selectedOptions.datesToLoad.maxTimestamp,
			dataType: config.dataType,
			parCodes: config.parCodes,
			calPars: config.calPars,
		};
		beginLoad(dataOptions);
	};

	const loadInfo = () => {
		if (!selectedOptions) return;
		loadingOptions.current = loading;
		if (loading) {
			if (
				loading.loadedMin &&
				loading.loadedMin < selectedOptions.datesToLoad.minTimestamp
			) {
				setLoading(null);
				return;
			}
			if (config?.dataType?.match(/.*CAL/)) {
				const payload = {
					precision: loading.precision,
					minTimestamp: loading.minTimestamp,
					maxTimestamp: loading.maxTimestamp,
					loadedMin: loading.loadedMin,
					loadedMax: loading.loadedMax,
					dataType: loading.dataType,
					calPars: loading.calPars,
				};
				sendPostRequest(
					`${DATA_URL}/stackplot/info/${loading.selectedSite}`,
					payload,
					(response) => {
						const numPages = response.data.numPages;
						const pageSize = response.data.pageSize;
						setLoadingProgress(0);
						columns.current = response.data.columns;
						if (numPages > 0) {
							loadPage(
								1,
								numPages,
								pageSize,
								loading,
								getDataForCurrentConfig(),
							);
						} else {
							setLoading(null);
							reloadStackPlotData();
						}
					},
					(error) => {
						setLoading(null);
					},
				);
			} else {
				const payload = {
					precision: loading.precision,
					minTimestamp: loading.minTimestamp,
					maxTimestamp: loading.maxTimestamp,
					loadedMin: loading.loadedMin,
					loadedMax: loading.loadedMax,
					dataType: loading.dataType,
					parCodes: loading.parCodes,
				};
				sendPostRequest(
					`${DATA_URL}/stackplot/info/${loading.selectedSite}`,
					payload,
					(response) => {
						const numPages = response.data.numPages;
						const pageSize = response.data.pageSize;
						setLoadingProgress(0);
						columns.current = response.data.columns;
						if (numPages > 0) {
							loadPage(
								1,
								numPages,
								pageSize,
								loading,
								getDataForCurrentConfig(),
							);
							// if (batchData[currentIndex]?.length) {
							// 	loadPage(1, numPages, pageSize, loading, getDataForCurrentConfig());
							// } else {
							// 	loadPage(1, numPages, pageSize, loading);
							// }
						} else {
							setLoading(null);
							reloadStackPlotData();
						}
					},
					(error) => {
						setLoading(null);
					},
				);
			}
		}
	};

	const loadPage = (
		pageNumber,
		numPages,
		pageSize,
		dataOptions,
		tempData = {},
	) => {
		if (
			JSON.stringify(loadingOptions.current) ===
			JSON.stringify(dataOptions)
		) {
			if (config?.dataType?.match(/.*CAL/)) {
				const payload = {
					stackplotId: selectedConfig,
					precision: dataOptions.precision,
					page: pageNumber,
					pageSize: pageSize,
					numPages: numPages,
					loadedMin: dataOptions.loadedMin,
					loadedMax: dataOptions.loadedMax,
					minTimestamp: dataOptions.minTimestamp,
					maxTimestamp: dataOptions.maxTimestamp,
					dataType: dataOptions.dataType,
					calPars: dataOptions.calPars,
				};
				sendPostRequest(
					`${DATA_URL}/stackplot/cal/${config.idNo}`,
					payload,
					(response) => {
						if (
							JSON.stringify(loadingOptions.current) ===
							JSON.stringify(dataOptions)
						) {
							const responseData = response.data;

							tempData = { ...tempData, ...responseData.data };
							setDataForCurrentConfig(tempData);
							setLoadingProgress(pageNumber / numPages);
							if (pageNumber < numPages) {
								loadPage(
									pageNumber + 1,
									numPages,
									pageSize,
									dataOptions,
									tempData,
								);
							} else {
								setLoading(null);
								if (!selectedOptions.sameConfig) {
									setSelectedOptions((values) => ({
										...values,
										sameConfig: true,
									}));
								}
							}
						}
					},
					(error) => {
						setLoading(null);
					},
				);
			} else {
				const payload = {
					stackplotId: selectedConfig,
					precision: dataOptions.precision,
					page: pageNumber,
					pageSize: pageSize,
					numPages: numPages,
					loadedMin: dataOptions.loadedMin,
					loadedMax: dataOptions.loadedMax,
					minTimestamp: dataOptions.minTimestamp,
					maxTimestamp: dataOptions.maxTimestamp,
					dataType: dataOptions.dataType,
					parCodes: dataOptions.parCodes,
				};
				sendPostRequest(
					`${DATA_URL}/stackplot/${config.idNo}`,
					payload,
					(response) => {
						if (
							JSON.stringify(loadingOptions.current) ===
							JSON.stringify(dataOptions)
						) {
							const responseData = response.data;

							tempData = { ...tempData, ...responseData.data };
							setDataForCurrentConfig(tempData);
							setLoadingProgress(pageNumber / numPages);
							if (pageNumber < numPages) {
								loadPage(
									pageNumber + 1,
									numPages,
									pageSize,
									dataOptions,
									tempData,
								);
							} else {
								setLoading(null);
								if (!selectedOptions.sameConfig) {
									setSelectedOptions((values) => ({
										...values,
										sameConfig: true,
									}));
								}
							}
						}
					},
					(error) => {
						setLoading(null);
					},
				);
			}
		}
	};

	const getLoadedMin = (data) => {
		if (!data) return null;
		const timestamps = Object.keys(data);
		if (!timestamps.length) return null;
		return getMin(timestamps);
	};

	const getLoadedMax = (data) => {
		if (!data) return null;
		const timestamps = Object.keys(data);
		if (!timestamps.length) return null;
		return getMax(timestamps);
	};

	const getMinDate = (data) => {
		if (!stackplotData || !stackplotData._timestamps) return 0;
		return moment
			.unix(stackplotData._timestamps[0])
			.format("YYYY-MM-DDTHH:mm");
	};

	const getMaxDate = (data) => {
		if (!stackplotData || !stackplotData._timestamps) return 0;
		return moment
			.unix(
				stackplotData._timestamps[stackplotData._timestamps.length - 1],
			)
			.format("YYYY-MM-DDTHH:mm");
	};

	const getMin = (data) => {
		if (data.length === 0) return -Infinity;
		else if (data.length === 1) return data[0];
		else {
			let min = data[0];
			for (let i = 1; i < data.length; i++) {
				if (data[i] < min) min = data[i];
			}
			return min;
		}
	};

	const getMax = (data) => {
		if (data.length === 0) return Infinity;
		else if (data.length === 1) return data[0];
		else {
			let max = data[0];
			for (let i = 1; i < data.length; i++) {
				if (data[i] > max) max = data[i];
			}
			return max;
		}
	};

	const explodeData = (rawData) => {
		if (!config?.dataType || !rawData || !columns.current) return null;
		const explodedData = {};
		if (config?.dataType?.match(/.*CAL/)) {
			for (let i = 0; i < columns.current.length; i++) {
				if (!Array.isArray(columns.current[i])) return null;
				if (!columns.current[i][0].match(/_(FLAG|CODE)$/)) {
					explodedData[columns.current[i]] = {
						columnIndex: i,
					};
				}
			}
		} else {
			for (let i = 0; i < columns.current.length; i++) {
				if (Array.isArray(columns.current[i])) return null;
				if (!columns.current[i].match(/_(FLAG|CODE)$/)) {
					explodedData[columns.current[i]] = {
						columnIndex: i,
					};
				}
			}
		}

		let minTimestamp = selectedOptions.datesToLoad.minTimestamp;
		let maxTimestamp = selectedOptions.datesToLoad.maxTimestamp;
		let timestamps = [];
		let interval = INTERVALS[config?.precision || "hourly"];
		for (let i = 0; i <= (maxTimestamp - minTimestamp) / interval; i++) {
			timestamps.push(minTimestamp + i * interval);
		}

		for (let timestamp of timestamps) {
			let row = rawData[timestamp];
			if (row) {
				for (let parCode in explodedData) {
					explodedData[parCode][timestamp] = [
						row[explodedData[parCode].columnIndex],
						row[explodedData[parCode].columnIndex + 1],
					];
				}
			}
		}
		explodedData._timestamps = timestamps;
		return explodedData;
	};

	const updateTimeframe = (minTimestamp, maxTimestamp) => {
		setSelectedOptions((values) => ({
			...values,
			datesToLoad: {
				minTimestamp: minTimestamp,
				maxTimestamp: maxTimestamp,
			},
		}));
	};

	const registerCallback = (callbackName, callbackFn) => {
		setCallbacks((values) => ({ ...values, [callbackName]: callbackFn }));
	};

	const getStackPlotNaturalHeight = (plotConfig) => {
		const charts = plotConfig?.charts || [];

		if (!charts.length) {
			return 0;
		}

		const wrapperBorderWidth = 1;
		const labelHeight = 15;
		const labelBottomMargin = -3;
		const labelEffectiveHeight = labelHeight + labelBottomMargin;

		const configuredSpacing = Number(plotConfig.chartSpacing) || 20;

		/*
		 * StackPlot chart spacing includes space occupied by chart borders
		 * and parameter labels. Remove that fixed content so that the
		 * calculated height matches the actual rendered plot more closely.
		 */
		const effectiveSpacing = Math.max(
			0,
			configuredSpacing - 2 * wrapperBorderWidth - labelEffectiveHeight,
		);

		const chartsHeight = charts.reduce(
			(total, chart) => total + (Number(chart.chartHeight) || 200),
			0,
		);

		/*
		 * Spacing only exists between charts.
		 */
		const spacingHeight = Math.max(0, charts.length - 1) * effectiveSpacing;

		const xAxisHeight = 40;
		const titleHeight = plotConfig.fullTitle ? 25 : 0;
		const footnoteHeight = plotConfig.footnote ? 25 : 0;

		return (
			chartsHeight +
			spacingHeight +
			xAxisHeight +
			titleHeight +
			footnoteHeight
		);
	};

	const calculatePopoutZoom = (
		popupWindow,
		plotConfig,
		controlsHeight = 45,
	) => {
		const availableHeight = Math.max(
			1,
			popupWindow.innerHeight - controlsHeight,
		);

		const naturalHeight = getStackPlotNaturalHeight(plotConfig);

		if (naturalHeight <= 0 || naturalHeight <= availableHeight) {
			return 100;
		}

		const calculatedZoom = (availableHeight / naturalHeight) * 100;

		/*
		 * Keep the automatic zoom at a multiple of 10%.
		 */
		const truncatedZoom = Math.floor(calculatedZoom / 10) * 10;

		return Math.max(10, Math.min(100, truncatedZoom));
	};

	const handlePopout = () => {
		if (popoutWindow) {
			const existingWindow = popoutWindow.ownerDocument?.defaultView;

			if (existingWindow && !existingWindow.closed) {
				existingWindow.focus();
				return;
			}

			setPopoutWindow(null);
		}

		const width = window.screen.availWidth - 100;
		const height = window.screen.availHeight - 100;

		const newWindow = window.open(
			"",
			"stackplot-popout",
			[
				`width=${Math.max(width, 800)}`,
				`height=${Math.max(height, 600)}`,
				"resizable=yes",
				"scrollbars=no",
			].join(","),
		);

		if (!newWindow) {
			alert("Popup blocked! Please allow popups for this site.");
			return;
		}

		newWindow.document.title = "Stack Batch";

		document
			.querySelectorAll('style, link[rel="stylesheet"]')
			.forEach((node) => {
				newWindow.document.head.appendChild(node.cloneNode(true));
			});

		newWindow.document.body.style.margin = "0";
		newWindow.document.body.style.width = "100%";
		newWindow.document.body.style.height = "100vh";
		newWindow.document.body.style.overflow = "hidden";

		const contentDiv = newWindow.document.createElement("div");

		contentDiv.id = "stackplot-popout-root";
		contentDiv.style.width = "100%";
		contentDiv.style.height = "100vh";

		newWindow.document.body.appendChild(contentDiv);

		newWindow.requestAnimationFrame(() => {
			const initialZoom = calculatePopoutZoom(newWindow, config);

			setPopoutZoomLevel(initialZoom);
			setPopoutWindow(contentDiv);

			if (callbacks.setTableSize) {
				callbacks.setTableSize("full");
			}
		});

		newWindow.addEventListener("beforeunload", () => {
			setPopoutWindow(null);
			setPopoutZoomLevel(100);

			if (callbacks.setTableSize) {
				callbacks.setTableSize("half");
			}
		});
	};

	return (
		<div id="stack-batch">
			<div id="stack-batch-controls">
				<div className="stack-batch-control-wrapper">
					<Label for="batch">Batch:</Label>
					<SearchableSelect
						data={batchList}
						selected={selectedBatch}
						setSelected={setSelectedBatch}
						valField={"batchId"}
						textField={"batchName"}
						name="batch"
					/>
				</div>
				<div className="stack-batch-control-wrapper">
					<Label for="plot">Plot:</Label>
					<SearchableSelect
						data={plots}
						selected={selectedConfig}
						setSelected={handlePlotSelect}
						valField={"stackPlotId"}
						textField={"plotName"}
						name="plot"
						sorted={false}
					/>
				</div>
				<Button
					className="stack-batch-control-button"
					onClick={handlePrev}
					disabled={currentIndex === -1 || currentIndex === 0}
				>
					Prev
				</Button>
				<Button
					className="stack-batch-control-button"
					onClick={handleNext}
					disabled={
						currentIndex === -1 || currentIndex === plots.length - 1
					}
				>
					Next
				</Button>
				<div className="stack-batch-control-wrapper">
					<Label for="timeframe">Timeframe:</Label>
					<StackPlotTimeFrameSelector
						config={config}
						updateTimeframe={updateTimeframe}
					/>
				</div>
				<Button
					id="stack-batch-popout-button"
					className="stack-batch-control-button"
					onClick={handlePopout}
					disabled={!config || !stackplotData}
				>
					Popout
				</Button>
				<Button
					id="stack-batch-refresh-button"
					className="stack-batch-control-button"
					onClick={reloadData}
					disabled={loading || !config}
				>
					{utilIcons.refresh(colors["ars-neutral-900"])}
				</Button>
			</div>
			<div id="stackplot-controls-help">
				<div className="stackplot-controls-help-blurb">
					<b>Drag:</b> Pan X Axis (all charts)
				</div>

				<div className="stackplot-controls-help-blurb">
					<b>Ctrl+Scroll:</b> Zoom X Axis (all charts)
				</div>

				<div className="stackplot-controls-help-blurb">
					<b>Shift+Drag:</b> Pan Y Axis (single chart)
				</div>

				<div className="stackplot-controls-help-blurb">
					<b>Shift+Scroll:</b> Zoom Y Axis (single chart)
				</div>

				<div className="stackplot-controls-help-blurb">
					<b>Shift+Dbl Click:</b> Reset Y Axis (single chart)
				</div>
			</div>
			<div id="stack-batch-main">
				{!popoutWindow && (
					<div className="stackplot-y-axis-toggle-wrapper">
						<PlotZoomControls
							zoomLevel={zoomLevel}
							setZoomLevel={setZoomLevel}
						/>
					</div>
				)}
				{popoutWindow ? (
					createPortal(
						<div id="stackplots" className="stackplot-popout">
							<div id="stackplot-popout-controls">
								<div className="stackplot-popout-navigation">
									<Button
										onClick={handlePrev}
										disabled={
											currentIndex === -1 ||
											currentIndex === 0
										}
									>
										Prev
									</Button>

									<span className="stackplot-popout-plot-position">
										{currentIndex >= 0 && plots.length
											? `${currentIndex + 1} of ${plots.length}`
											: ""}
									</span>

									<Button
										onClick={handleNext}
										disabled={
											currentIndex === -1 ||
											currentIndex === plots.length - 1
										}
									>
										Next
									</Button>
								</div>

								<span className="stackplot-popout-plot-name">
									{plots[currentIndex]?.plotName ||
										config?.plotName ||
										""}
								</span>

								<PlotZoomControls
									zoomLevel={popoutZoomLevel}
									setZoomLevel={setPopoutZoomLevel}
								/>
							</div>

							<div
								id="stackplot-popout-help"
								className="stackplot-popout-help"
							>
								<div id="stackplot-controls-help">
									<div className="stackplot-controls-help-blurb">
										<b>Drag:</b> Pan X Axis (all charts)
									</div>

									<div className="stackplot-controls-help-blurb">
										<b>Ctrl+Scroll:</b> Zoom X Axis (all
										charts)
									</div>

									<div className="stackplot-controls-help-blurb">
										<b>Shift+Drag:</b> Pan Y Axis (single
										chart)
									</div>

									<div className="stackplot-controls-help-blurb">
										<b>Shift+Scroll:</b> Zoom Y Axis (single
										chart)
									</div>

									<div className="stackplot-controls-help-blurb">
										<b>Shift+Dbl Click:</b> Reset Y Axis
										(single chart)
									</div>
								</div>
							</div>

							<div id="stackplot-outer-wrapper">
								<StackPlot
									data={stackplotData}
									config={config}
									dateRange={dateRange}
									callbacks={callbacks}
									zoomLevel={popoutZoomLevel}
									isPopout={true}
								/>
							</div>

							<LoadingScreen
								hidden={!loading}
								loadingProgress={loadingProgress}
								isMobile={checkMobile()}
							/>
						</div>,
						popoutWindow,
					)
				) : (
					<div id="stackplot-outer-wrapper">
						<StackPlot
							data={stackplotData}
							config={config}
							dateRange={dateRange}
							stackPlotRef={stackPlotRef}
							printable={printMode}
							bubbleChartRefs={setChartRefs}
							callbacks={callbacks}
							zoomLevel={zoomLevel}
						/>
					</div>
				)}
				<StackPlotTable
					data={stackplotData}
					config={config}
					registerCallback={registerCallback}
				/>
				<LoadingScreen
					hidden={!loading}
					loadingProgress={loadingProgress}
					isMobile={checkMobile()}
				/>
			</div>
		</div>
	);
}

export default StackBatch;
